/**
 * Bilingual dictionary for Cadena Zero.
 *
 * RULE: every user-facing string in the app (UI text, toasts, dialogs, emails)
 * MUST exist in BOTH languages. Add the English phrase as the key and the
 * Spanish translation as the value in `esFromEn`. If a component is written in
 * Spanish, add the reverse entry in `enFromEs`.
 */

export const esFromEn: Record<string, string> = {
  // --- Navigation / layout ---
  "Home": "Inicio",
  "Suppliers": "Proveedores",
  "Categories": "Categorías",
  "Assessments": "Evaluaciones",
  "Tools": "Herramientas",
  "Deploy": "Desplegar",
  "Plans": "Planes",
  "Results": "Resultados",
  "Reporting": "Reportes",
  "Engagement": "Participación",
  "Events": "Eventos",
  "Resources": "Recursos",
  "AI Assistant": "Asistente IA",
  "My Profile": "Mi perfil",
  "Assigned Tools": "Herramientas asignadas",
  "Performance": "Desempeño",
  "Community": "Comunidad",
  "Help & Support": "Ayuda y soporte",
  "Settings": "Configuración",
  "Sign Out": "Cerrar sesión",
  "Expand menu": "Expandir menú",
  "Collapse menu": "Contraer menú",
  "My Ecosystem": "Mi ecosistema",
  "Powered by Cadena Zero": "Desarrollado por Cadena Zero",
  "Admin": "Administrador",
  "Profile": "Perfil",
  "Language": "Idioma",
  "English": "Inglés",
  "Spanish": "Español",
  "Choose the language for the interface and the emails you receive.":
    "Elige el idioma de la interfaz y de los correos que recibes.",
  "Language updated": "Idioma actualizado",
  "Could not update language": "No se pudo actualizar el idioma",

  // --- Generic actions / states ---
  "Save": "Guardar",
  "Saving...": "Guardando...",
  "Creating...": "Creando...",
  "Save Profile": "Guardar perfil",
  "Submit": "Enviar",
  "Submitted": "Enviado",
  "Cancel": "Cancelar",
  "Delete": "Eliminar",
  "Edit": "Editar",
  "Done": "Listo",
  "Export": "Exportar",
  "Invite": "Invitar",
  "Bulk": "Masivo",
  "Custom": "Personalizado",
  "None": "Ninguno",
  "Not set": "Sin definir",
  "Note": "Nota",
  "Note:": "Nota:",
  "Total": "Total",
  "Status": "Estado",
  "Type": "Tipo",
  "Title": "Título",
  "Title *": "Título *",
  "Name": "Nombre",
  "Description": "Descripción",
  "Value": "Valor",
  "Unit": "Unidad",
  "Units": "Unidades",
  "Action": "Acción",
  "Actions": "Acciones",
  "Change": "Cambio",
  "% Change": "% de cambio",
  "Score": "Puntaje",
  "Score:": "Puntaje:",
  "Sent": "Enviado",
  "Failed": "Fallido",
  "Pending": "Pendiente",
  "Completed": "Completado",
  "Live": "En vivo",
  "Latest": "Más reciente",
  "Past": "Pasados",
  "Upcoming": "Próximos",
  "Search...": "Buscar...",
  "Next page": "Página siguiente",
  "Previous page": "Página anterior",
  "Error": "Error",
  "Are you sure?": "¿Estás seguro?",
  "Loading...": "Cargando...",
  "Loading…": "Cargando…",
  "Sending…": "Enviando…",
  "Uploading…": "Subiendo…",
  "Copied": "Copiado",
  "Link copied": "Enlace copiado",
  "Link copied to clipboard.": "Enlace copiado al portapapeles.",
  "Could not copy. Select and copy manually.": "No se pudo copiar. Selecciona y copia manualmente.",
  "An unexpected error occurred.": "Ocurrió un error inesperado.",
  "Please refresh the page.": "Por favor recarga la página.",
  "Some data may not be available. Please refresh.": "Algunos datos podrían no estar disponibles. Recarga la página.",
  "Missing fields": "Faltan campos",
  "Cannot save": "No se puede guardar",
  "Oops! Page not found": "¡Ups! Página no encontrada",
  "© 2026 Cadena Zero. All rights reserved.": "© 2026 Cadena Zero. Todos los derechos reservados.",

  // --- Auth ---
  "Email": "Correo electrónico",
  "Account Email": "Correo de la cuenta",
  "Password": "Contraseña",
  "Confirm Password": "Confirmar contraseña",
  "Full Name": "Nombre completo",
  "Full name": "Nombre completo",
  "Your full name": "Tu nombre completo",
  "Welcome!": "¡Bienvenido!",
  "Welcome to the Ecosystem": "Bienvenido al ecosistema",
  "Account created!": "¡Cuenta creada!",
  "Password created!": "¡Contraseña creada!",
  "Password updated!": "¡Contraseña actualizada!",
  "Password must be at least 6 characters": "La contraseña debe tener al menos 6 caracteres",
  "Check your email": "Revisa tu correo",
  "Check your email to confirm your account, then sign in.":
    "Revisa tu correo para confirmar tu cuenta y luego inicia sesión.",
  "We sent you a password reset link.": "Te enviamos un enlace para restablecer tu contraseña.",
  "Please sign in with your new password.": "Inicia sesión con tu nueva contraseña.",
  "You can now sign in with your new password.": "Ya puedes iniciar sesión con tu nueva contraseña.",
  "Verifying your link…": "Verificando tu enlace…",
  "Loading authorization request…": "Cargando la solicitud de autorización…",
  "Authorization error": "Error de autorización",
  "Sign in — Cadena Zero": "Iniciar sesión — Cadena Zero",
  "Reset password — Cadena Zero": "Restablecer contraseña — Cadena Zero",
  "you@company.com": "tu@empresa.com",
  "Your profile has been set up.": "Tu perfil ha sido configurado.",

  // --- Organization / settings ---
  "Organization": "Organización",
  "Organization Name": "Nombre de la organización",
  "Organization name updated!": "¡Nombre de la organización actualizado!",
  "Team Members": "Miembros del equipo",
  "Invite Team Member": "Invitar miembro del equipo",
  "Team member email": "Correo del miembro del equipo",
  "Full name (optional)": "Nombre completo (opcional)",
  "Send Invitation": "Enviar invitación",
  "Sending...": "Enviando...",
  "Team member removed": "Miembro del equipo eliminado",
  "Remove team member": "Eliminar miembro del equipo",
  "Remove team member?": "¿Eliminar miembro del equipo?",
  "Removing...": "Eliminando...",
  "Remove": "Eliminar",
  "Pending invite": "Invitación pendiente",
  "Active": "Activo",
  "No team members found.": "No se encontraron miembros del equipo.",
  "No name": "Sin nombre",
  "Team Member": "Miembro del equipo",
  "Already in organization": "Ya está en la organización",
  "Could not send invitation.": "No se pudo enviar la invitación.",
  "Could not remove team member.": "No se pudo eliminar al miembro del equipo.",
  "Manage your ecosystem organization and team members.":
    "Gestiona tu organización del ecosistema y los miembros del equipo.",
  "This person will join your ecosystem as an admin with full management capabilities.":
    "Esta persona se unirá a tu ecosistema como administrador con capacidades completas de gestión.",
  "Supplier Ecosystem · Powered by Cadena Zero": "Ecosistema de proveedores · Desarrollado por Cadena Zero",
  "Tool Visibility & Permissions": "Visibilidad y permisos de herramientas",
  "Control which categories can see and access each tool. Only checked categories will have visibility.":
    "Controla qué categorías pueden ver y acceder a cada herramienta. Solo las categorías marcadas tendrán visibilidad.",
  "Logo updated!": "¡Logo actualizado!",
  "Upload failed": "Falló la carga",
  "Assign tools to categories": "Asignar herramientas a categorías",

  // --- Suppliers / directory ---
  "Supplier": "Proveedor",
  "Invite Supplier": "Invitar proveedor",
  "Invite a Supplier": "Invitar a un proveedor",
  "Supplier email": "Correo del proveedor",
  "Supplier email *": "Correo del proveedor *",
  "Company": "Empresa",
  "Company Name": "Nombre de la empresa",
  "Company name": "Nombre de la empresa",
  "Company name *": "Nombre de la empresa *",
  "Company name required": "El nombre de la empresa es obligatorio",
  "Company / Organization": "Empresa / Organización",
  "Company Profile": "Perfil de la empresa",
  "Company Size": "Tamaño de la empresa",
  "Company description...": "Descripción de la empresa...",
  "Contact Name": "Nombre de contacto",
  "Contact name": "Nombre de contacto",
  "Country": "País",
  "Location": "Ubicación",
  "Website": "Sitio web",
  "Sector / Industry": "Sector / Industria",
  "Manage your supplier directory": "Gestiona tu directorio de proveedores",
  "Supplier deleted": "Proveedor eliminado",
  "Delete supplier": "Eliminar proveedor",
  "Could not delete supplier": "No se pudo eliminar el proveedor",
  "Supplier recategorized": "Proveedor recategorizado",
  "Recategorize Supplier": "Recategorizar proveedor",
  "Supplier removed from category": "Proveedor eliminado de la categoría",
  "Supplier Engagement": "Participación de proveedores",
  "Suppliers Evaluated": "Proveedores evaluados",
  "Suppliers by Category": "Proveedores por categoría",
  "Suppliers without a category assigned": "Proveedores sin categoría asignada",
  "No suppliers": "Sin proveedores",
  "No suppliers found": "No se encontraron proveedores",
  "No suppliers found. Use \"Invite Supplier\" to add suppliers.":
    "No se encontraron proveedores. Usa \"Invitar proveedor\" para agregarlos.",
  "No suppliers match your search.": "Ningún proveedor coincide con tu búsqueda.",
  "No suppliers assigned yet.": "Aún no hay proveedores asignados.",
  "No suppliers in this category yet.": "Aún no hay proveedores en esta categoría.",
  "Loading suppliers...": "Cargando proveedores...",
  "Error loading suppliers": "Error al cargar los proveedores",
  "Search by company name...": "Buscar por nombre de empresa...",
  "Search by name, company, or email...": "Buscar por nombre, empresa o correo...",
  "Search suppliers by name, company, or email": "Buscar proveedores por nombre, empresa o correo",
  "Search suppliers by name...": "Buscar proveedores por nombre...",
  "Search supplier...": "Buscar proveedor...",
  "Selected Supplier": "Proveedor seleccionado",
  "Select at least one supplier": "Selecciona al menos un proveedor",
  "Add supplier": "Agregar proveedor",
  "Assign Suppliers": "Asignar proveedores",
  "Manual Assignments": "Asignaciones manuales",
  "Invitation access link": "Enlace de acceso de la invitación",
  "An invitation email will be sent and a copyable access link will be shown as backup.":
    "Se enviará un correo de invitación y se mostrará un enlace de acceso copiable como respaldo.",
  "Sending invitations...": "Enviando invitaciones...",
  "Bulk Invite": "Invitación masiva",
  "Bulk Invite Suppliers": "Invitación masiva de proveedores",
  "Drop your CSV file here": "Suelta tu archivo CSV aquí",
  "or click to browse": "o haz clic para buscar",
  "No valid rows": "No hay filas válidas",
  "No valid rows found": "No se encontraron filas válidas",
  "Ensure the CSV has email and category columns.":
    "Asegúrate de que el CSV tenga columnas de correo y categoría.",
  "Create categories first. The CSV must include a category column.":
    "Crea categorías primero. El CSV debe incluir una columna de categoría.",
  "Fix validation errors or remap categories before sending.":
    "Corrige los errores de validación o reasigna las categorías antes de enviar.",
  "Remap category...": "Reasignar categoría...",
  "Please enter the company name.": "Ingresa el nombre de la empresa.",
  "Please choose a category for this supplier.": "Elige una categoría para este proveedor.",

  // --- Categories / segments ---
  "Category": "Categoría",
  "Category *": "Categoría *",
  "New Category": "Nueva categoría",
  "Create a new category": "Crear una nueva categoría",
  "Category name": "Nombre de la categoría",
  "Category description": "Descripción de la categoría",
  "Category created": "Categoría creada",
  "Category updated": "Categoría actualizada",
  "Category deleted": "Categoría eliminada",
  "Category already exists": "La categoría ya existe",
  "Another category already uses this name.": "Otra categoría ya usa este nombre.",
  "Name already in use": "El nombre ya está en uso",
  "Error deleting category": "Error al eliminar la categoría",
  "Error moving suppliers": "Error al mover proveedores",
  "Error uncategorizing suppliers": "Error al quitar la categoría de los proveedores",
  "Move suppliers to another category": "Mover proveedores a otra categoría",
  "Uncategorize all suppliers": "Quitar la categoría a todos los proveedores",
  "Uncategorized": "Sin categoría",
  "No category": "Sin categoría",
  "No category assigned": "Sin categoría asignada",
  "Category assigned by your admin": "Categoría asignada por tu administrador",
  "All Categories": "Todas las categorías",
  "All categories": "Todas las categorías",
  "No categories available.": "No hay categorías disponibles.",
  "No categories created yet": "Aún no se han creado categorías",
  "No categories yet": "Aún no hay categorías",
  "Select a category": "Selecciona una categoría",
  "Select category...": "Seleccionar categoría...",
  "Select a target category": "Selecciona la categoría destino",
  "Select target category...": "Seleccionar categoría destino...",
  "Target Categories": "Categorías objetivo",
  "Category Auto-Suggestion Rules": "Reglas de sugerencia automática de categoría",
  "Create categories first to set up auto-suggestion rules.":
    "Crea categorías primero para configurar reglas de sugerencia automática.",
  "Create categories in the Categories section first.":
    "Crea categorías primero en la sección Categorías.",
  "Strategic supplier groups": "Grupos estratégicos de proveedores",
  "Export categories to Excel": "Exportar categorías a Excel",
  "Suggest category:": "Sugerir categoría:",
  "Suggested:": "Sugerida:",
  "When ALL conditions match:": "Cuando TODAS las condiciones coincidan:",
  "+ Add condition": "+ Agregar condición",
  "Remove condition": "Eliminar condición",
  "Remove rule": "Eliminar regla",
  "Used for self-categorization.": "Se usa para la autocategorización.",

  // --- Tools / assessments ---
  "Tool Builder": "Constructor de herramientas",
  "Tool name": "Nombre de la herramienta",
  "Tool deleted": "Herramienta eliminada",
  "Delete tool": "Eliminar herramienta",
  "Edit tool": "Editar herramienta",
  "Tool not found": "Herramienta no encontrada",
  "Tool not found.": "Herramienta no encontrada.",
  "Tool Completion": "Finalización de herramientas",
  "Tools Used": "Herramientas usadas",
  "All Tools": "Todas las herramientas",
  "Select tool...": "Seleccionar herramienta...",
  "Search tools...": "Buscar herramientas...",
  "Search tools or suppliers...": "Buscar herramientas o proveedores...",
  "Search assessments...": "Buscar evaluaciones...",
  "Loading tools...": "Cargando herramientas...",
  "Loading tool...": "Cargando herramienta...",
  "No tools yet.": "Aún no hay herramientas.",
  "No tools assigned yet": "Aún no hay herramientas asignadas",
  "No tools assigned yet.": "Aún no hay herramientas asignadas.",
  "Pending Tools": "Herramientas pendientes",
  "Questions": "Preguntas",
  "Question text": "Texto de la pregunta",
  "Remove question": "Eliminar pregunta",
  "Option text": "Texto de la opción",
  "+ Add option": "+ Agregar opción",
  "Remove option": "Eliminar opción",
  "Option 1": "Opción 1",
  "Option 2": "Opción 2",
  "Multiple Choice:": "Opción múltiple:",
  "Name and at least one question required.": "Se requiere un nombre y al menos una pregunta.",
  "New Assessment": "Nueva evaluación",
  "Assessment created!": "¡Evaluación creada!",
  "Assessment updated!": "¡Evaluación actualizada!",
  "Assessment deleted": "Evaluación eliminada",
  "Assessment Results": "Resultados de la evaluación",
  "Assessment emails": "Correos de la evaluación",
  "Toggle assessment emails": "Activar o desactivar correos de la evaluación",
  "Controls assignment, automatic, overdue, and manual reminder emails for this assessment.":
    "Controla los correos de asignación, automáticos, vencidos y recordatorios manuales de esta evaluación.",
  "The assessment has been deleted successfully.": "La evaluación se eliminó correctamente.",
  "Recent Assessments": "Evaluaciones recientes",
  "Completed Assessments": "Evaluaciones completadas",
  "No assessments completed yet.": "Aún no se han completado evaluaciones.",
  "Create one to gather info from new suppliers.":
    "Crea una para recopilar información de los nuevos proveedores.",
  "Create one to start evaluating your suppliers.":
    "Crea una para empezar a evaluar a tus proveedores.",
  "Onboarding surveys for suppliers": "Encuestas de incorporación para proveedores",
  "Surveys & assessments": "Encuestas y evaluaciones",
  "Survey": "Encuesta",
  "Initial": "Inicial",
  "Checklist": "Lista de verificación",
  "KPI": "KPI",
  "KPI Assessment": "Evaluación de KPI",
  "KPIs": "KPIs",
  "KPIs & Scoring": "KPIs y puntuación",
  "Key Performance Indicators": "Indicadores clave de desempeño",
  "Scored Evaluation": "Evaluación puntuada",
  "Scoring Rules": "Reglas de puntuación",
  "Performance Scoring": "Puntuación de desempeño",
  "Max Score": "Puntaje máximo",
  "Max score:": "Puntaje máximo:",
  "Min score:": "Puntaje mínimo:",
  "Max points:": "Puntos máximos:",
  "Maximum possible points:": "Puntos máximos posibles:",
  "Weight:": "Peso:",
  "Avg Score": "Puntaje promedio",
  "Average Score": "Puntaje promedio",
  "Average Score by Category": "Puntaje promedio por categoría",
  "Average Score by Tool": "Puntaje promedio por herramienta",
  "Overall Score": "Puntaje general",
  "No assessments yet": "Aún no hay evaluaciones",
  "No assessments yet.": "Aún no hay evaluaciones.",
  "No assessments match your search.": "Ninguna evaluación coincide con tu búsqueda.",
  "Action required": "Acción requerida",
  "All done": "Todo listo",
  "1 assessment completed": "1 evaluación completada",
  "My Score": "Mi puntaje",
  "Score Distribution": "Distribución de puntajes",
  "Score ≥ 85": "Puntaje ≥ 85",
  "Scores & evaluations": "Puntajes y evaluaciones",
  "Scorecards": "Tarjetas de puntuación",
  "Unrated": "Sin calificar",
  "Needs Improvement": "Necesita mejorar",
  "Best Performer": "Mejor desempeño",
  "Top Performers": "Mejores desempeños",
  "Compliance Tools": "Herramientas de cumplimiento",
  "Impact Management": "Gestión de impacto",
  "Modular Architecture": "Arquitectura modular",

  // --- Deploy / reminders ---
  "Deploy Tool": "Desplegar herramienta",
  "New Deployment": "Nuevo despliegue",
  "Active Deployments": "Despliegues activos",
  "No deployments yet.": "Aún no hay despliegues.",
  "Deployment created but failed to assign suppliers":
    "El despliegue se creó pero no se pudieron asignar los proveedores",
  "Pick a due date": "Elige una fecha límite",
  "Pick a due date for this deployment": "Elige una fecha límite para este despliegue",
  "Due in": "Vence en",
  "Automatic reminders": "Recordatorios automáticos",
  "Send reminder": "Enviar recordatorio",
  "Send assessment reminder": "Enviar recordatorio de evaluación",
  "Reminder sent": "Recordatorio enviado",
  "days after assignment": "días después de la asignación",

  // --- Results / AI review ---
  "No results yet": "Aún no hay resultados",
  "No responses": "Sin respuestas",
  "No pending responses": "No hay respuestas pendientes",
  "No completed responses yet": "Aún no hay respuestas completadas",
  "No completed responses yet.": "Aún no hay respuestas completadas.",
  "No completed supplier scores yet.": "Aún no hay puntajes de proveedores completados.",
  "No responses for this tool yet.": "Aún no hay respuestas para esta herramienta.",
  "Total Responses": "Respuestas totales",
  "Responses will appear here after suppliers complete assigned tools.":
    "Las respuestas aparecerán aquí cuando los proveedores completen las herramientas asignadas.",
  "There are no tool responses to export yet.": "Aún no hay respuestas para exportar.",
  "Loading results...": "Cargando resultados...",
  "View answers": "Ver respuestas",
  "Run AI Review": "Ejecutar revisión con IA",
  "AI Review": "Revisión con IA",
  "AI Review Result": "Resultado de la revisión con IA",
  "AI Review failed": "Falló la revisión con IA",
  "AI Reviewing...": "Analizando con IA...",
  "AI Document Review:": "Revisión de documentos con IA:",
  "AI review could not be completed, but the file was saved.":
    "No se pudo completar la revisión con IA, pero el archivo se guardó.",
  "Key Findings:": "Hallazgos clave:",
  "Red Flags:": "Señales de alerta:",
  "Evidence reviewed": "Evidencia revisada",
  "Evidence uploaded": "Evidencia cargada",
  "Supplier will upload a document or image. AI will review and score it automatically.":
    "El proveedor subirá un documento o imagen. La IA lo revisará y puntuará automáticamente.",

  // --- Files ---
  "File *": "Archivo *",
  "File too large": "Archivo demasiado grande",
  "File uploaded!": "¡Archivo cargado!",
  "Invalid file": "Archivo inválido",
  "Error uploading file": "Error al subir el archivo",
  "Click to select a file": "Haz clic para seleccionar un archivo",
  "Max 20MB": "Máx. 20 MB",
  "Maximum file size is 20MB": "El tamaño máximo del archivo es 20 MB",
  "Max size is 5 MB.": "El tamaño máximo es 5 MB.",
  "Please upload an image.": "Sube una imagen.",
  "Upload company photo": "Subir foto de la empresa",
  "Photo updated!": "¡Foto actualizada!",
  "PDF, DOCX, XLSX, MP4, etc.": "PDF, DOCX, XLSX, MP4, etc.",
  "Export failed": "Falló la exportación",
  "Export ready": "Exportación lista",

  // --- Plans ---
  "Improvement Plans": "Planes de mejora",
  "Improvement Plan Template": "Plantilla de plan de mejora",
  "Supplier improvement plans": "Planes de mejora de proveedores",
  "Plan Details": "Detalles del plan",
  "Plan Items": "Elementos del plan",
  "Plan name": "Nombre del plan",
  "Plan name is required": "El nombre del plan es obligatorio",
  "Plan deleted": "Plan eliminado",
  "Delete this improvement plan?": "¿Eliminar este plan de mejora?",
  "This will remove all items and assignments. This action cannot be undone.":
    "Esto eliminará todos los elementos y asignaciones. Esta acción no se puede deshacer.",
  "Error saving plan": "Error al guardar el plan",
  "No plans assigned": "No hay planes asignados",
  "No plans yet": "Aún no hay planes",
  "Your improvement plans will appear here once assigned.":
    "Tus planes de mejora aparecerán aquí una vez asignados.",
  "Create improvement plans with action items, resources, and milestones for your suppliers.":
    "Crea planes de mejora con acciones, recursos e hitos para tus proveedores.",
  "Action Item": "Acción",
  "Milestone": "Hito",
  "Target": "Meta",
  "Target value": "Valor objetivo",
  "Target Tiers": "Niveles objetivo",
  "Binary Target": "Meta binaria",
  "Numeric Target": "Meta numérica",
  "Yes / No target with evidence": "Meta de Sí / No con evidencia",
  "Yes label": "Etiqueta para Sí",
  "No label": "Etiqueta para No",
  "Actual:": "Real:",
  "Template for creating improvement plans.": "Plantilla para crear planes de mejora.",

  // --- Tiers ---
  "Tier": "Nivel",
  "Current Tier": "Nivel actual",
  "Tier Distribution": "Distribución de niveles",
  "Tier Distribution by Category": "Distribución de niveles por categoría",
  "All Tiers": "Todos los niveles",
  "No tiers defined yet": "Aún no hay niveles definidos",
  "Click here to add your first tier": "Haz clic aquí para agregar tu primer nivel",
  "Remove tier": "Eliminar nivel",
  "Gold": "Oro",
  "Gold Tier": "Nivel oro",
  "Silver": "Plata",
  "Bronze": "Bronce",
  "Color:": "Color:",

  // --- Dashboard / reporting ---
  "Performance Dashboard": "Panel de desempeño",
  "My Performance": "Mi desempeño",
  "Performance by Sector": "Desempeño por sector",
  "Reporting & Benchmarks": "Reportes y comparativas",
  "Benchmarks": "Comparativas",
  "Ecosystem reports": "Reportes del ecosistema",
  "Engagement & trends": "Participación y tendencias",
  "Daily Engagement": "Participación diaria",
  "Live Activity": "Actividad en vivo",
  "Actions Today": "Acciones de hoy",
  "Total Interactions": "Interacciones totales",
  "Avg. Session": "Sesión promedio",
  "Tracking": "Seguimiento",
  "Across all active tools": "En todas las herramientas activas",
  "Error loading dashboard": "Error al cargar el panel",
  "Loading charts...": "Cargando gráficas...",
  "No supplier data available for charts.": "No hay datos de proveedores para las gráficas.",
  "No scored suppliers in this category.": "No hay proveedores puntuados en esta categoría.",
  "Complete your assigned tools to see your scores here.":
    "Completa tus herramientas asignadas para ver tus puntajes aquí.",
  "Profile completion": "Perfil completado",
  "Error loading profile": "Error al cargar el perfil",
  "Profile saved": "Perfil guardado",
  "Profile saved!": "¡Perfil guardado!",
  "Loading supplier profile...": "Cargando perfil del proveedor...",
  "Describe your company, capabilities, certifications, and sustainability initiatives...":
    "Describe tu empresa, capacidades, certificaciones e iniciativas de sostenibilidad...",
  "No description added yet. Tell the ecosystem about your company.":
    "Aún no hay descripción. Cuéntale al ecosistema sobre tu empresa.",
  "No description provided.": "No se proporcionó descripción.",
  "Your role in the company": "Tu rol en la empresa",
  "e.g. Sustainability Manager": "p. ej. Gerente de Sostenibilidad",

  // --- Community ---
  "Community Feed": "Muro de la comunidad",
  "Write a comment...": "Escribe un comentario...",
  "✍️ Write a post": "✍️ Escribir una publicación",
  "Share an update or start a discussion...": "Comparte una novedad o inicia una conversación...",
  "Share an announcement, recognize a supplier, or start a discussion...":
    "Comparte un anuncio, reconoce a un proveedor o inicia una conversación...",
  "Share something with your ecosystem ✨": "Comparte algo con tu ecosistema ✨",
  "No posts yet.": "Aún no hay publicaciones.",
  "No posts yet. Be the first to share!": "Aún no hay publicaciones. ¡Sé el primero en compartir!",
  "No posts yet. Create the first announcement!": "Aún no hay publicaciones. ¡Crea el primer anuncio!",
  "Could not create post": "No se pudo crear la publicación",
  "Thank you! Excited to keep improving.": "¡Gracias! Con muchas ganas de seguir mejorando.",

  // --- Events ---
  "Upcoming Events": "Próximos eventos",
  "Past Events": "Eventos pasados",
  "Event title": "Título del evento",
  "Event description...": "Descripción del evento...",
  "No events yet.": "Aún no hay eventos.",
  "Create your first event to get started.": "Crea tu primer evento para comenzar.",
  "Add to calendar": "Agregar al calendario",
  "Date *": "Fecha *",
  "Time": "Hora",
  "Max Attendees": "Asistentes máximos",
  "attending": "asistiendo",
  "attended": "asistieron",
  "attendees": "asistentes",
  "Invite / Meeting Link": "Enlace de invitación / reunión",
  "Workshop": "Taller",
  "Webinar": "Webinar",
  "Training": "Capacitación",
  "Conference": "Conferencia",
  "Workshops & events": "Talleres y eventos",
  "e.g. 10:00 AM - 4:00 PM": "p. ej. 10:00 - 16:00",
  "e.g. Virtual – Zoom, or physical address": "p. ej. Virtual – Zoom, o dirección física",

  // --- Resources ---
  "Resource": "Recurso",
  "Resource Repository": "Repositorio de recursos",
  "Resource title": "Título del recurso",
  "Search resources...": "Buscar recursos...",
  "No resources yet.": "Aún no hay recursos.",
  "Upload your first resource to share with suppliers.":
    "Sube tu primer recurso para compartirlo con los proveedores.",
  "Documents & knowledge": "Documentos y conocimiento",
  "Brief description...": "Descripción breve...",

  // --- Filters ---
  "All Countries": "Todos los países",
  "All Locations": "Todas las ubicaciones",
  "All Sectors": "Todos los sectores",
  "Select location...": "Seleccionar ubicación...",
  "Select sector...": "Seleccionar sector...",
  "Select size...": "Seleccionar tamaño...",

  // --- Help ---
  "Type your question...": "Escribe tu pregunta...",
  "Could not update emails": "No se pudieron actualizar los correos",
  "About": "Acerca de",
  "Company Details": "Datos de la empresa",
  "Showcase your organization to the ecosystem": "Muestra tu organización al ecosistema",
  "Access documents, guides, and tools": "Accede a documentos, guías y herramientas",
  "Aggregate supplier performance analytics": "Analítica agregada del desempeño de proveedores",
  "Analyze scores across categories and suppliers": "Analiza puntuaciones por categorías y proveedores",
  "Announcements, recognition, and engagement": "Anuncios, reconocimiento y participación",
  "Ask questions or get redirected to your admin team": "Haz preguntas o contacta con tu equipo administrador",
  "Assessment name (e.g. Supplier Assessment 2026)": "Nombre del assessment (ej. Assessment de Proveedores 2026)",
  "Assign tools to supplier categories": "Asigna herramientas a categorías de proveedores",
  "Avg. Session Duration (weekly)": "Duración media de sesión (semanal)",
  "Build the onboarding survey for new suppliers": "Crea el cuestionario de incorporación para nuevos proveedores",
  "Click to upload a document (PDF, DOCX, CSV, etc.)": "Haz clic para subir un documento (PDF, DOCX, CSV, etc.)",
  "Close": "Cerrar",
  "Start": "Comenzar",
  "Complete the tools assigned to your segments": "Completa las herramientas asignadas a tus segmentos",
  "Contact name (optional)": "Nombre de contacto (opcional)",
  "Create and manage modular tools": "Crea y gestiona herramientas modulares",
  "Create development packages to help suppliers improve performance": "Crea paquetes de desarrollo para ayudar a los proveedores a mejorar",
  "Create supplier groups and assign members": "Crea grupos de proveedores y asigna miembros",
  "Customize metrics and scoring engines": "Personaliza métricas y motores de puntuación",
  "Define items, targeting, and assignments": "Define elementos, segmentación y asignaciones",
  "Description (optional)": "Descripción (opcional)",
  "Feature Usage (%)": "Uso de funcionalidades (%)",
  "Location Link (map)": "Enlace de ubicación (mapa)",
  "Manage workshops, webinars, and conferences": "Gestiona talleres, webinars y conferencias",
  "Manage your supplier ecosystem": "Gestiona tu ecosistema de proveedores",
  "Monitor active users, interactions, and platform activity": "Monitorea usuarios activos, interacciones y actividad de la plataforma",
  "Next": "Siguiente",
  "Previous": "Anterior",
  "Onboarding surveys that suppliers must complete first": "Cuestionarios de incorporación que los proveedores deben completar primero",
  "Passing Score (%):": "Puntuación mínima (%):",
  "Register for workshops, webinars, and conferences": "Inscríbete en talleres, webinars y conferencias",
  "Review supplier responses and assign categories": "Revisa las respuestas de los proveedores y asigna categorías",
  "Score Trend (6 months)": "Tendencia de puntuación (6 meses)",
  "Share documents, guides, and tools with suppliers": "Comparte documentos, guías y herramientas con los proveedores",
  "Stay connected with your value chain network": "Mantente conectado con tu red de cadena de valor",
  "Tier name (e.g. Gold, Premium, Level A)": "Nombre del nivel (ej. Oro, Premium, Nivel A)",
  "Toggle Sidebar": "Mostrar/ocultar menú lateral",
  "Track your scores and assessment results": "Consulta tus puntuaciones y resultados de assessments",
  "Update assessment questions and category rules": "Actualiza las preguntas del assessment y las reglas de categorías",
  "View and analyze supplier responses grouped by tool": "Consulta y analiza las respuestas agrupadas por herramienta",
  "Weight (%):": "Peso (%):",
  "Your assigned development plans and action items": "Tus planes de desarrollo asignados y acciones",
  "Your supplier dashboard": "Tu panel de proveedor",
  "days": "días",
  "Tons": "Toneladas",

  // --- Sweep: remaining UI strings ---
  "AI Document Review": "Revisión de documentos con IA",
  "AI reviewing…": "Revisando con IA…",
  "AI-powered assistant · If needed, we'll connect you with your admin team":
    "Asistente con IA · Si es necesario, te conectamos con tu equipo administrador",
  "Add Rule": "Agregar regla",
  "Add Supplier": "Agregar proveedor",
  "Add Tier": "Agregar nivel",
  "Add action items, resources, milestones, targets, or notes above":
    "Agrega acciones, recursos, hitos, metas o notas arriba",
  "All Types": "Todos los tipos",
  "Annual Supplier Conference": "Conferencia anual de proveedores",
  "Approve": "Aprobar",
  "Are you sure you want to delete": "¿Seguro que quieres eliminar",
  "Assigned Plans": "Planes asignados",
  "Back": "Atrás",
  "Back to Categories": "Volver a categorías",
  "Back to Suppliers": "Volver a proveedores",
  "Back to sign in": "Volver a iniciar sesión",
  "Binary KPI": "KPI binario",
  "Calculator for Scope 1, 2, and 3 emissions.": "Calculadora de emisiones de Alcance 1, 2 y 3.",
  "Carbon Footprint Calculator": "Calculadora de huella de carbono",
  "Carbon Footprint Training": "Capacitación en huella de carbono",
  "Change file": "Cambiar archivo",
  "Clear": "Limpiar",
  "Complete": "Completo",
  "Complete Profile": "Completar perfil",
  "Complete your profile": "Completa tu perfil",
  "Compliance Update Webinar": "Webinar de actualización normativa",
  "Comprehensive guide to ESG reporting standards.": "Guía completa de estándares de reporte ESG.",
  "Continue": "Continuar",
  "Copy": "Copiar",
  "Copy link": "Copiar enlace",
  "Couldn't send reminder": "No se pudo enviar el recordatorio",
  "Create": "Crear",
  "Create Event": "Crear evento",
  "Create account": "Crear cuenta",
  "Create category": "Crear categoría",
  "Create your first category to group suppliers, then invite suppliers to join it.":
    "Crea tu primera categoría para agrupar proveedores y luego invítalos a unirse.",
  "Deny": "Denegar",
  "Deploy this assessment": "Desplegar este assessment",
  "Download .ics (Outlook/Apple)": "Descargar .ics (Outlook/Apple)",
  "Download template": "Descargar plantilla",
  "Download uploaded file": "Descargar archivo subido",
  "Due date": "Fecha límite",
  "ESG Reporting Framework 2026": "Marco de reporte ESG 2026",
  "Edit Scoring Rule": "Editar regla de puntuación",
  "Event added to calendar!": "¡Evento agregado al calendario!",
  "Expected format": "Formato esperado",
  "Export All": "Exportar todo",
  "Export CSV": "Exportar CSV",
  "Export Excel": "Exportar Excel",
  "File Upload": "Carga de archivo",
  "Forgot password?": "¿Olvidaste tu contraseña?",
  "Google Calendar": "Google Calendar",
  "Hands-on workshop covering ESG implementation strategies.":
    "Taller práctico sobre estrategias de implementación ESG.",
  "Hover the photo to upload a company logo.": "Pasa el cursor sobre la foto para subir el logo de la empresa.",
  "Invite suppliers": "Invitar proveedores",
  "Link": "Enlace",
  "Loading permissions...": "Cargando permisos...",
  "Map": "Mapa",
  "Multiple Choice": "Opción múltiple",
  "Networking, recognition, and strategic alignment.": "Networking, reconocimiento y alineación estratégica.",
  "New KPI": "Nuevo KPI",
  "New Plan": "Nuevo plan",
  "New Tool": "Nueva herramienta",
  "No categories created yet. Create categories first to configure visibility.":
    "Aún no hay categorías. Crea categorías primero para configurar la visibilidad.",
  "No tools created yet. Create tools first to configure visibility.":
    "Aún no hay herramientas. Crea herramientas primero para configurar la visibilidad.",
  "Open resource": "Abrir recurso",
  "Passwords don't match": "Las contraseñas no coinciden",
  "Pinned": "Fijado",
  "Please complete your company profile to get the most out of the platform.":
    "Completa el perfil de tu empresa para aprovechar al máximo la plataforma.",
  "Progress: Assessment vs. Latest Evaluations": "Progreso: assessment vs. últimas evaluaciones",
  "Publish": "Publicar",
  "Rating": "Calificación",
  "Recategorize": "Recategorizar",
  "Register": "Inscribirse",
  "Registered": "Inscrito",
  "Require AI review of uploaded document": "Requerir revisión con IA del documento subido",
  "Require a passing score": "Requerir puntuación mínima",
  "Require evidence upload": "Requerir carga de evidencia",
  "Require evidence upload with AI review": "Requerir carga de evidencia con revisión de IA",
  "Return to Home": "Volver al inicio",
  "Select all": "Seleccionar todo",
  "Send assessment emails for this assessment": "Enviar correos de assessment para este assessment",
  "Sign in": "Iniciar sesión",
  "Step-by-step guide for new suppliers.": "Guía paso a paso para nuevos proveedores.",
  "Successfully registered for the event!": "¡Inscripción al evento exitosa!",
  "Supplier Onboarding Guide": "Guía de incorporación de proveedores",
  "Supplier Responses": "Respuestas del proveedor",
  "Supplier uploads a document, AI scores it automatically.":
    "El proveedor sube un documento y la IA lo califica automáticamente.",
  "Sustainability Best Practices": "Buenas prácticas de sostenibilidad",
  "Sustainability Workshop 2026": "Taller de sostenibilidad 2026",
  "Tell us a bit about yourself to get started in your ecosystem.":
    "Cuéntanos un poco sobre ti para comenzar en tu ecosistema.",
  "Text": "Texto",
  "Tier Definitions": "Definiciones de niveles",
  "Tool / Category": "Herramienta / Categoría",
  "Training on the Carbon Footprint Calculator.": "Capacitación sobre la calculadora de huella de carbono.",
  "Transfer": "Transferir",
  "Updates on regulatory changes in LATAM markets.":
    "Actualizaciones sobre cambios regulatorios en los mercados de LATAM.",
  "Upload File": "Subir archivo",
  "Upload Resource": "Subir recurso",
  "Upload a CSV file with supplier emails to send multiple invitations at once.":
    "Sube un archivo CSV con correos de proveedores para enviar varias invitaciones a la vez.",
  "Upload evidence (PDF, image, doc)": "Sube evidencia (PDF, imagen, documento)",
  "Value Chain Impact Platform": "Plataforma de impacto en la cadena de valor",
  "Video training on sustainability practices.": "Capacitación en video sobre prácticas de sostenibilidad.",
  "View": "Ver",
  "View Answers": "Ver respuestas",
  "View evidence": "Ver evidencia",
  "You are about to delete": "Estás a punto de eliminar",
  "You can revoke this connection at any time from your account settings.":
    "Puedes revocar esta conexión en cualquier momento desde la configuración de tu cuenta.",
  "Your Ecosystem": "Tu ecosistema",
  "Your private value chain ecosystem": "Tu ecosistema privado de cadena de valor",
  "https:// (video, PDF, course, article...)": "https:// (video, PDF, curso, artículo...)",

  // --- Sweep: remaining English strings ---
  "We'll email": "Enviaremos un correo a",
  "a friendly reminder to complete their pending assessment.":
    "con un recordatorio amable para completar su evaluación pendiente.",
  "this supplier": "este proveedor",
  "Low Score (<60)": "Puntaje bajo (<60)",
  "Score < 50": "Puntaje < 50",

  // --- Sweep: remaining untranslated strings ---
  "(you)": "(tú)",
  "+ Add option (max 5)": "+ Añadir opción (máx. 5)",
  "Active Users (7d)": "Usuarios activos (7d)",
  "All suppliers are already assigned to a category. Use \"Recategorize\" on a supplier to move them here.":
    "Todos los proveedores ya están asignados a una categoría. Usa \"Recategorizar\" en un proveedor para moverlo aquí.",
  "ASSESSMENT": "EVALUACIÓN",
  "Define tiers based on assessment score ranges. Suppliers will be classified into tiers automatically.":
    "Define niveles según los rangos de puntaje de la evaluación. Los proveedores se clasificarán en niveles automáticamente.",
  "Email sent": "Correo enviado",
  "Invitation sent": "Invitación enviada",
  "Link ready": "Enlace listo",
  "Supplier must confirm completion and upload supporting evidence. AI will review the uploaded document.":
    "El proveedor debe confirmar la finalización y subir evidencia de respaldo. La IA revisará el documento subido.",
  "Visibility rules do not control assignment. To assign an assessment to suppliers, use the Deploy page.":
    "Las reglas de visibilidad no controlan la asignación. Para asignar una evaluación a proveedores, usa la página Desplegar.",
  "contains": "contiene",
  "equals": "es igual a",
  "e.g. 20": "ej. 20",

  // --- Sweep 2: auth, admin pages, supplier pages, dialogs ---
  "Welcome back": "Bienvenido de nuevo",
  "Enter your email and we'll send you a reset link":
    "Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña",
  "Please wait...": "Espera un momento...",
  "Invalid invitation link": "Enlace de invitación no válido",
  "Welcome to the ecosystem": "Bienvenido al ecosistema",
  "Set your new password": "Establece tu nueva contraseña",
  "Set a secure password to access your supplier dashboard.":
    "Establece una contraseña segura para acceder a tu panel de proveedor.",
  "Enter your new password below.": "Ingresa tu nueva contraseña a continuación.",
  "Create Password": "Crear contraseña",
  "Update Password": "Actualizar contraseña",
  ". Your invitation or reset link has expired or was already used. Please request a new one.":
    ". Tu invitación o enlace de restablecimiento ha expirado o ya fue usado. Solicita uno nuevo.",
  "Something went wrong": "Algo salió mal",
  "Assessment name is required.": "El nombre de la evaluación es obligatorio.",
  "Add at least one question.": "Agrega al menos una pregunta.",
  "Assessment emails enabled": "Correos de evaluación habilitados",
  "Assessment emails disabled": "Correos de evaluación deshabilitados",
  "Plan created": "Plan creado",
  "Plan updated": "Plan actualizado",
  "Edit Plan": "Editar plan",
  "New Improvement Plan": "Nuevo plan de mejora",
  "Uploading...": "Subiendo...",
  "Click to upload PDF, video, image, etc.": "Haz clic para subir PDF, video, imagen, etc.",
  "Tool saved!": "¡Herramienta guardada!",
  "Tool updated!": "¡Herramienta actualizada!",
  "Edit Tool": "Editar herramienta",
  "Update this tool's configuration": "Actualiza la configuración de esta herramienta",
  "Exporting...": "Exportando...",
  "Export Responses": "Exportar respuestas",
  "Create categories first": "Crea categorías primero",
  "Supplier added": "Proveedor agregado",
  "This will permanently delete an empty category. This action cannot be undone.":
    "Esto eliminará permanentemente una categoría vacía. Esta acción no se puede deshacer.",
  "Category details": "Detalles de la categoría",
  "Select uncategorized suppliers...": "Seleccionar proveedores sin categoría...",
  "No details": "Sin detalles",
  "No sector": "Sin sector",
  "Unknown Tool": "Herramienta desconocida",
  "Resend invitation": "Reenviar invitación",
  "Send a new access link": "Enviar un nuevo enlace de acceso",
  "Send access link": "Enviar enlace de acceso",
  "Change category": "Cambiar categoría",
  "Assign category": "Asignar categoría",
  "Uploaded compliance document": "Subió un documento de cumplimiento",
  "Commented on community post": "Comentó en una publicación de la comunidad",
  "Viewed performance report": "Vio un reporte de desempeño",
  "Updated company profile": "Actualizó el perfil de la empresa",
  "Deselect all": "Deseleccionar todo",
  "Complete assessments": "Completa evaluaciones",
  "Response submitted": "Respuesta enviada",
  "✓ Response submitted!": "✓ ¡Respuesta enviada!",
  "Email required": "Correo requerido",
  "Invalid email format": "Formato de correo no válido",
  "Category required": "Categoría requerida",
  "Some invitations failed. Check results for links and errors.":
    "Algunas invitaciones fallaron. Revisa los resultados para ver enlaces y errores.",
  "All invitations were sent successfully!": "¡Todas las invitaciones se enviaron correctamente!",
  "Category Name": "Nombre de la categoría",
  "Email sent to the supplier. Link copied to clipboard as backup.":
    "Correo enviado al proveedor. Enlace copiado al portapapeles como respaldo.",
  "Invitation email sent to the supplier.": "Correo de invitación enviado al proveedor.",
  "Invitation processed.": "Invitación procesada.",
  "Just now": "Justo ahora",

  // --- Chart axis labels (day/month abbreviations) ---
  "Mon": "lun",
  "Tue": "mar",
  "Wed": "mié",
  "Thu": "jue",
  "Fri": "vie",
  "Sat": "sáb",
  "Sun": "dom",
  "Jan": "ene",
  "Feb": "feb",
  "Sep": "sep",
  "Oct": "oct",
  "Nov": "nov",
  "Dec": "dic",

  // --- Sweep 3: auth headings, tool/plan actions, fallbacks, taxonomies ---
  "Already in ecosystem": "Ya está en el ecosistema",
  "Application": "Aplicación",
  "Assign suppliers": "Asignar proveedores",
  "Based on your scores": "Según tus puntajes",
  "Check a cell to grant that category visibility to the tool. Unchecked means the category cannot see the tool.":
    "Marca una celda para otorgar a esa categoría visibilidad sobre la herramienta. Si no está marcada, la categoría no podrá ver la herramienta.",
  "Comment": "Comentario",
  "Comments": "Comentarios",
  "Create Account": "Crear cuenta",
  "Create Your Password": "Crea tu contraseña",
  "Create an admin account to manage your supplier ecosystem":
    "Crea una cuenta de administrador para gestionar tu ecosistema de proveedores",
  "Create surveys, evaluations & KPI assessments with scoring":
    "Crea encuestas, evaluaciones y assessments de KPI con puntuación",
  "Create your account": "Crea tu cuenta",
  "Deleting...": "Eliminando...",
  "Delete Supplier": "Eliminar proveedor",
  "Delete Tool": "Eliminar herramienta",
  "Deploying...": "Desplegando...",
  "Evaluation": "Evaluación",
  "Evidence": "Evidencia",
  "Failed to load authorization request": "No se pudo cargar la solicitud de autorización",
  "Failed to load suppliers": "No se pudieron cargar los proveedores",
  "Failed to save plan": "No se pudo guardar el plan",
  "Failed to send": "No se pudo enviar",
  "File": "Archivo",
  "General": "General",
  "Get Started": "Comenzar",
  "Guidelines": "Guías",
  "Improvement Plan": "Plan de mejora",
  "In Progress": "En progreso",
  "Intake": "Incorporación",
  "Invitation link": "Enlace de invitación",
  "My Organization": "Mi organización",
  "Network error": "Error de red",
  "No": "No",
  "No description": "Sin descripción",
  "No email": "Sin correo",
  "No redirect returned by the authorization server.":
    "El servidor de autorización no devolvió una redirección.",
  "Off": "Desactivado",
  "On": "Activado",
  "Onboarding": "Incorporación",
  "Pin": "Fijar",
  "Please try again.": "Inténtalo de nuevo.",
  "Post": "Publicar",
  "Posting…": "Publicando…",
  "Reset Password": "Restablecer contraseña",
  "Reset your password": "Restablece tu contraseña",
  "Save Assessment": "Guardar assessment",
  "Save category": "Guardar categoría",
  "Save Plan": "Guardar plan",
  "Save Tool": "Guardar herramienta",
  "Send Reset Link": "Enviar enlace de restablecimiento",
  "Service temporarily unavailable. Please try again later.":
    "Servicio no disponible temporalmente. Inténtalo de nuevo más tarde.",
  "Set a new password for your Cadena Zero account or complete your invitation.":
    "Establece una nueva contraseña para tu cuenta de Cadena Zero o completa tu invitación.",
  "Set a new password for your Cadena Zero account.":
    "Establece una nueva contraseña para tu cuenta de Cadena Zero.",
  "Sign In": "Iniciar sesión",
  "Sign in to Cadena Zero or accept your invitation to access supplier engagement, assessments, and improvement plans.":
    "Inicia sesión en Cadena Zero o acepta tu invitación para acceder a la participación de proveedores, assessments y planes de mejora.",
  "Sign in to Cadena Zero or accept your invitation.":
    "Inicia sesión en Cadena Zero o acepta tu invitación.",
  "Sign in to your ecosystem": "Inicia sesión en tu ecosistema",
  "Submitting...": "Enviando...",
  "Templates": "Plantillas",
  "This document requires AI compliance review": "Este documento requiere revisión de cumplimiento con IA",
  "This supplier profile could not be found. It may have been deleted or you may not have access.":
    "No se pudo encontrar este perfil de proveedor. Es posible que haya sido eliminado o que no tengas acceso.",
  "Too many requests. Please wait a moment and try again.":
    "Demasiadas solicitudes. Espera un momento e inténtalo de nuevo.",
  "Unknown": "Desconocido",
  "Unknown error": "Error desconocido",
  "Unnamed": "Sin nombre",
  "Unnamed Organization": "Organización sin nombre",
  "Unnamed Plan": "Plan sin nombre",
  "Unnamed Supplier": "Proveedor sin nombre",
  "Unnamed supplier": "proveedor sin nombre",
  "Update Assessment": "Actualizar assessment",
  "Update Tool": "Actualizar herramienta",
  "User": "Usuario",
  "Virtual / Remote": "Virtual / Remoto",
  "Yes": "Sí",
  "You": "Tú",
  "Your Company": "Tu empresa",
  "You've been invited to join a sustainability ecosystem. Create your password to get started.":
    "Has sido invitado a unirte a un ecosistema de sostenibilidad. Crea tu contraseña para comenzar.",
  "Administrator": "Administrador",

  // --- Sweep 3: company size options ---
  "1-10 employees": "1-10 empleados",
  "11-50 employees": "11-50 empleados",
  "51-200 employees": "51-200 empleados",
  "201-500 employees": "201-500 empleados",
  "501-1000 employees": "501-1000 empleados",
  "1000+ employees": "Más de 1000 empleados",

  // --- Sweep 3: sector / industry options ---
  "Agriculture": "Agricultura",
  "Automotive": "Automotriz",
  "Construction": "Construcción",
  "Consumer Goods": "Bienes de consumo",
  "Electronics": "Electrónica",
  "Energy": "Energía",
  "Financial Services": "Servicios financieros",
  "Food & Beverage": "Alimentos y bebidas",
  "Healthcare": "Salud",
  "Logistics": "Logística",
  "Manufacturing": "Manufactura",
  "Mining": "Minería",
  "Packaging": "Empaque",
  "Retail": "Comercio minorista",
  "Technology": "Tecnología",
  "Textiles": "Textiles",
  "Transportation": "Transporte",
  "Other": "Otro",

};



/**
 * Reverse direction: source strings written in Spanish that must be shown in
 * English when the interface language is English.
 */
export const enFromEs: Record<string, string> = {
  "Cancelar": "Cancel",
  "Ahorra tiempo": "Save time",
  "Aumenta eficiencia": "Increase efficiency",
  "Conecta": "Connect",
  "Evalúa": "Evaluate",
  "Mejora": "Improve",
  "Mejores decisiones": "Better decisions",
  "Menos tiempo en evaluaciones": "Less time on assessments",
  "Organizaciones activas": "Active organizations",
  "Para quién": "Who it's for",
  "Por qué es diferente": "Why it's different",
  "Proveedores gestionados": "Suppliers managed",
  "Qué hace": "What it does",
  "Reduce complejidad": "Reduce complexity",
  "en un solo lugar.": "in a single place.",
  "Equipos de procurement, sostenibilidad y supply chain que gestionan múltiples proveedores.":
    "Procurement, sustainability and supply chain teams managing multiple suppliers.",
  "El título es obligatorio.": "The title is required.",
  "El título y la fecha son obligatorios.": "Title and date are required.",
  "Evento creado exitosamente": "Event created successfully",
  "Evento eliminado": "Event deleted",
  "Recurso eliminado": "Resource deleted",
  "Recurso subido exitosamente": "Resource uploaded successfully",
  "Selecciona un archivo para subir.": "Select a file to upload.",
  "Proveedor asignado a la categoría": "Supplier assigned to the category",
  "¿Eliminar assessment?": "Delete assessment?",
  "¿Eliminar evento?": "Delete event?",
  "¿Eliminar recurso?": "Delete resource?",

  // --- Sweep: landing page & Spanish dialogs ---
  "Acceder": "Sign in",
  "Acceder →": "Sign in →",
  "Comenzar": "Get started",
  "Comenzar ahora": "Get started now",
  "Ver cómo funciona": "See how it works",
  "Cómo funciona": "How it works",
  "Beneficios que importan": "Benefits that matter",
  "Empresas que confían en Cadena Zero": "Companies that trust Cadena Zero",
  "Todo lo que necesitas para gobernar tu cadena":
    "Everything you need to govern your supply chain",
  "Herramientas modulares diseñadas para equipos de procurement y sostenibilidad.":
    "Modular tools designed for procurement and sustainability teams.",
  "Tres pasos para transformar la gestión de tu cadena de suministro.":
    "Three steps to transform your supply chain management.",
  "¿Listo para transformar tu cadena de suministro?":
    "Ready to transform your supply chain?",
  "Únete a las organizaciones que ya gestionan proveedores, evaluaciones e impacto desde un solo lugar.":
    "Join the organizations already managing suppliers, assessments and impact from a single place.",
  "Estás a punto de eliminar": "You are about to delete",
  ". Se borrarán todas las respuestas asociadas. Esta acción no se puede deshacer.":
    ". All associated responses will be deleted. This action cannot be undone.",
  "Eliminar assessment": "Delete assessment",
  "Eliminando...": "Deleting...",
  "Eliminar": "Delete",

  // --- Sweep 3: landing page feature/testimonial copy ---
  "Onboard y gestiona proveedores en una red privada y segura.":
    "Onboard and manage suppliers in a private, secure network.",
  "Despliega encuestas, checklists y evaluaciones con plantillas listas.":
    "Deploy surveys, checklists, and evaluations with ready-made templates.",
  "KPIs y scoring personalizados por industria y objetivos.":
    "Custom KPIs and scoring by industry and goals.",
  "Planes de mejora accionables, trazables y con evidencia.":
    "Actionable, traceable improvement plans backed by evidence.",
  "Mide y reporta impacto ESG en toda tu cadena de valor.":
    "Measure and report ESG impact across your value chain.",
  "Construye solo lo que necesitas, sin bloat ni complejidad.":
    "Build only what you need, without bloat or complexity.",
  "Revisa resultados, puntuaciones y activa planes de mejora concretos.":
    "Review results, scores, and launch concrete improvement plans.",
  "Datos centralizados para reporting, ESG y gobierno de cadena.":
    "Centralized data for reporting, ESG, and supply chain governance.",
  "Una plataforma privada donde tu empresa y sus proveedores colaboran, completan evaluaciones y miden impacto.":
    "A private platform where your company and its suppliers collaborate, complete assessments, and measure impact.",

};
